import React, { useState, useMemo } from "react";
import { ShoppingCart, AlertCircle, Sparkles, Filter, Loader2 } from "lucide-react";
import { useCart } from "../components/CartContext";

const PRODUCTS = [
  {
    id: 1, title: "The Pilgrim's Progress", author: "John Bunyan",
    price: 49900, originalPrice: 79900, stock: 15,
    badge: "Bestseller", genre: "Classics",
    from: "#1C0F04", to: "#5A2E0A",
    desc: "The timeless allegory of the Christian journey from the City of Destruction to the Celestial City.",
    pages: 320,
  },
  {
    id: 2, title: "Confessions", author: "Augustine of Hippo",
    price: 59900, stock: 8,
    badge: "Classic", genre: "Theology",
    from: "#0F1E14", to: "#1E4A28",
    desc: "Augustine's autobiographical work recounting his sinful youth and his conversion to Christianity.",
    pages: 384,
  },
  {
    id: 3, title: "The Practice of the Presence of God", author: "Brother Lawrence",
    price: 29900, originalPrice: 39900, stock: 0,
    badge: "Devotional", genre: "Devotional",
    from: "#180A30", to: "#3D1A70",
    desc: "Simple letters describing how to cultivate an awareness of God in daily life.",
    pages: 128,
  },
  {
    id: 4, title: "Knowing God", author: "J.I. Packer",
    price: 69900, stock: 22,
    badge: "Modern", genre: "Theology",
    from: "#1E0804", to: "#6B2018",
    desc: "A comprehensive look at the nature and character of God and what it means to truly know Him.",
    pages: 288,
  },
  {
    id: 5, title: "Mere Christianity", author: "C.S. Lewis",
    price: 64900, originalPrice: 84900, stock: 18,
    badge: "Popular", genre: "Apologetics",
    from: "#080F1E", to: "#1A2A6B",
    desc: "Lewis's case for Christianity based on natural law — a modern apologetics landmark.",
    pages: 256,
  },
  {
    id: 6, title: "Desiring God", author: "John Piper",
    price: 74900, stock: 10,
    badge: null, genre: "Theology",
    from: "#1A0A0A", to: "#5A1818",
    desc: "Christian Hedonism — God is most glorified in us when we are most satisfied in Him.",
    pages: 336,
  },
  {
    id: 7, title: "Test Book (₹1)", author: "Ebenezer Team",
    price: 100, stock: 99,
    badge: "TEST", genre: "Test",
    from: "#0A0A0A", to: "#1A1A2E",
    desc: "Dummy product for payment gateway testing. Do not purchase.",
    pages: 1, freeShipping: true,
  },
];

const GENRES = ["All", ...Array.from(new Set(PRODUCTS.map((p) => p.genre)))];

function ProductCard({ product, onAdd, openCart }) {
  const [hovered,  setHovered]  = useState(false);
  const [loading,  setLoading]  = useState(false);

  const handleAdd = async (e) => {
    e.stopPropagation();
    if (product.stock === 0 || loading) return;
    setLoading(true);
    await onAdd(product, { open: false });
    setLoading(false);
    openCart();
  };

  const discount = product.originalPrice ? Math.round((1 - product.price / product.originalPrice) * 100) : 0;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "var(--card-bg)",
        border: `1px solid ${hovered ? "var(--card-hover-border)" : "var(--card-border)"}`,
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
        boxShadow: hovered ? "var(--shadow-lg)" : "var(--shadow)",
        transform: hovered ? "translateY(-5px)" : "none",
        transition: "all 0.25s cubic-bezier(0.34,1.56,0.64,1)",
        display: "flex", flexDirection: "column",
        animation: "fadeUp 0.4s ease both",
      }}
    >
      {/* Cover */}
      <div style={{
        height: "220px",
        background: `linear-gradient(155deg,${product.from} 0%,${product.to} 100%)`,
        position: "relative", overflow: "hidden",
        display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem",
      }}>
        <div style={{ position:"absolute",left:0,top:0,bottom:0,width:"6px",background:"rgba(0,0,0,0.3)" }} />
        <div style={{ position:"absolute",top:0,left:"6px",right:0,height:"40%",background:"linear-gradient(180deg,rgba(255,255,255,0.08) 0%,transparent 100%)" }} />

        {product.stock === 0 && (
          <div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:2 }}>
            <span style={{ background:"rgba(0,0,0,0.7)",color:"#fff",padding:"6px 16px",borderRadius:"var(--radius-full)",fontSize:"0.78rem",fontWeight:700 }}>Out of Stock</span>
          </div>
        )}

        <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:"12px",zIndex:1 }}>
          <div style={{ fontSize:"2.5rem",filter:"drop-shadow(0 4px 12px rgba(0,0,0,0.5))",transform:hovered?"scale(1.08)":"none",transition:"transform 0.3s" }}>📖</div>
          <div style={{ fontFamily:"var(--font-display)",color:"#fff",fontSize:"1.05rem",fontWeight:700,textAlign:"center",lineHeight:1.3,textShadow:"0 2px 10px rgba(0,0,0,0.5)" }}>{product.title}</div>
        </div>

        <div style={{ position:"absolute",top:"10px",right:"10px",display:"flex",flexDirection:"column",gap:"4px",alignItems:"flex-end",zIndex:3 }}>
          {product.badge && (
            <span style={{ background:"var(--gold)",color:"#fff",fontSize:"0.56rem",fontWeight:700,padding:"3px 9px",borderRadius:"var(--radius-full)",textTransform:"uppercase",letterSpacing:"0.08em" }}>{product.badge}</span>
          )}
          {discount > 0 && (
            <span style={{ background:"var(--rust)",color:"#fff",fontSize:"0.56rem",fontWeight:700,padding:"3px 9px",borderRadius:"var(--radius-full)" }}>-{discount}%</span>
          )}
        </div>
      </div>

      {/* Details */}
      <div style={{ padding:"1.1rem 1.1rem 0.9rem",flex:1,display:"flex",flexDirection:"column",gap:"8px" }}>
        <div>
          <div style={{ fontSize:"0.75rem",color:"var(--muted)",marginBottom:"4px" }}>{product.author}</div>
          <p style={{ fontSize:"0.78rem",color:"var(--muted)",lineHeight:1.5,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden" }}>
            {product.desc}
          </p>
        </div>

        <div style={{ display:"flex",gap:"8px",flexWrap:"wrap" }}>
          <span style={{ fontSize:"0.6rem",color:"var(--gold)",background:"var(--gold-faint)",border:"1px solid var(--gold-faint-2)",padding:"2px 8px",borderRadius:"var(--radius-full)",fontWeight:700,textTransform:"uppercase" }}>{product.genre}</span>
          <span style={{ fontSize:"0.6rem",color:"var(--muted)",background:"var(--cream-2)",padding:"2px 8px",borderRadius:"var(--radius-full)" }}>{product.pages} pages</span>
        </div>

        <div style={{ display:"flex",alignItems:"center",gap:"8px",marginTop:"auto",paddingTop:"10px",borderTop:"1px solid var(--card-border)" }}>
          <div style={{ flex:1 }}>
            <div style={{ display:"flex",alignItems:"baseline",gap:"6px" }}>
              <span style={{ fontFamily:"var(--font-display)",fontSize:"1.4rem",fontWeight:700,color:product.stock===0?"var(--muted)":"var(--ink)" }}>
                ₹{(product.price/100).toFixed(0)}
              </span>
              {product.originalPrice && (
                <span style={{ fontSize:"0.8rem",color:"var(--muted)",textDecoration:"line-through" }}>₹{(product.originalPrice/100).toFixed(0)}</span>
              )}
            </div>
            <div style={{ fontSize:"0.65rem",fontWeight:600,marginTop:"1px",color:product.stock===0?"var(--rust)":product.stock<5?"#D97706":"var(--sage)" }}>
              {product.stock===0 ? "Out of Stock" : product.stock < 5 ? `Only ${product.stock} left!` : "In Stock ✓"}
            </div>
          </div>

          <button onClick={handleAdd} disabled={product.stock === 0 || loading} style={{
            padding:"9px 16px",borderRadius:"var(--radius-full)",border:"none",
            background: product.stock===0 ? "var(--dust)" : "linear-gradient(135deg,var(--gold),var(--gold-2))",
            color: product.stock===0 ? "var(--muted)" : "#fff",
            fontSize:"0.78rem",fontWeight:700,cursor:(product.stock===0||loading)?"not-allowed":"pointer",
            display:"flex",alignItems:"center",gap:"6px",
            boxShadow: product.stock===0 ? "none" : "var(--shadow-gold)",
            transition:"all 0.2s",whiteSpace:"nowrap",
          }}
            onMouseEnter={(e)=>{ if(product.stock>0&&!loading) e.currentTarget.style.transform="scale(1.04)"; }}
            onMouseLeave={(e)=>{ e.currentTarget.style.transform=""; }}
          >
            {loading ? <><Loader2 size={13} className="spin" /> Adding...</> : <><ShoppingCart size={13} /> Add to Cart</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Store() {
  const { addToCart, openCart } = useCart();
  const [selectedGenre, setSelectedGenre] = useState("All");
  const [sortBy, setSortBy] = useState("default");

  const filtered = useMemo(() => {
    let list = PRODUCTS;
    if (selectedGenre !== "All") list = list.filter((p) => p.genre === selectedGenre);
    if (sortBy === "price-asc")  list = [...list].sort((a,b) => a.price - b.price);
    if (sortBy === "price-desc") list = [...list].sort((a,b) => b.price - a.price);
    return list;
  }, [selectedGenre, sortBy]);

  const inStock   = PRODUCTS.filter((p) => p.stock > 0).length;
  const fromPrice = Math.min(...PRODUCTS.map((p) => p.price));

  return (
    <>
      {/* ── HERO ── */}
      <section style={{ background:"var(--store-hero)",position:"relative",overflow:"hidden" }}>
        <div style={{ position:"absolute",inset:0,backgroundImage:"radial-gradient(rgba(255,255,255,0.025) 1px,transparent 1px)",backgroundSize:"32px 32px",pointerEvents:"none" }} />
        <div style={{ position:"absolute",top:"-80px",right:"8%",width:"300px",height:"300px",borderRadius:"50%",background:"radial-gradient(circle,rgba(58,102,69,0.2) 0%,transparent 70%)",pointerEvents:"none" }} />

        <div style={{ maxWidth:"var(--max-w)",margin:"0 auto",padding:"4rem 2rem 3.5rem",position:"relative",zIndex:1 }}>
          <div style={{ display:"inline-flex",alignItems:"center",gap:"7px",background:"rgba(74,144,96,0.18)",border:"1px solid rgba(74,144,96,0.3)",borderRadius:"var(--radius-full)",padding:"5px 14px",marginBottom:"1.25rem" }}>
            <Sparkles size={11} color="#6BCB8B" />
            <span style={{ fontSize:"0.68rem",fontWeight:700,color:"#6BCB8B",letterSpacing:"0.12em",textTransform:"uppercase" }}>Ebenezer Bookstore</span>
          </div>
          <h1 style={{ fontFamily:"var(--font-display)",color:"#fff",fontSize:"clamp(2rem,4vw,3rem)",fontWeight:400,lineHeight:1.15,marginBottom:"0.75rem" }}>
            Own <em style={{ fontStyle:"italic",color:"#6BCB8B" }}>Beautiful</em> Hardcopies
          </h1>
          <p style={{ color:"rgba(255,255,255,0.55)",fontSize:"0.95rem",lineHeight:1.7,maxWidth:"520px",marginBottom:"2rem" }}>
            High-quality printed editions of classic Christian literature — beautifully bound for your shelf or as a meaningful gift.
          </p>
          <div style={{ display:"flex",gap:"2rem",flexWrap:"wrap" }}>
            {[
              { label:`${inStock} Titles In Stock`, icon:"📦" },
              { label:`From ₹${(fromPrice/100).toFixed(0)}`, icon:"💰" },
              { label:"Free Shipping", icon:"🚚" },
            ].map(({ label, icon }) => (
              <div key={label} style={{ display:"flex",alignItems:"center",gap:"8px",fontSize:"0.82rem",color:"rgba(255,255,255,0.65)",fontWeight:500 }}>
                <span>{icon}</span>{label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STICKY FILTERS ── */}
      <div style={{ background:"var(--card-bg)",borderBottom:"1px solid var(--card-border)",position:"sticky",top:"var(--nav-h)",zIndex:50 }}>
        <div style={{ maxWidth:"var(--max-w)",margin:"0 auto" }}>

          {/* Genre pills — single scrollable row, never wraps */}
          <div style={{ display:"flex",alignItems:"center",gap:"0",borderBottom:"1px solid var(--card-border)" }}>
            <div style={{
              display:"flex",alignItems:"center",gap:"6px",
              padding:"0 0 0 1rem",
              color:"var(--muted)",fontSize:"0.75rem",fontWeight:600,
              flexShrink:0, borderRight:"1px solid var(--card-border)",
              height:"40px",
            }}>
              <Filter size={12} />
            </div>
            <div style={{
              display:"flex",gap:"6px",
              overflowX:"auto",padding:"8px 1rem",
              scrollbarWidth:"none", /* Firefox */
              msOverflowStyle:"none", /* IE/Edge */
              flex:1,
            }}
              className="eb-scroll-hide"
            >
              {GENRES.map((g) => (
                <button key={g} onClick={() => setSelectedGenre(g)} style={{
                  padding:"5px 14px",borderRadius:"var(--radius-full)",border:"1px solid",
                  borderColor: selectedGenre===g ? "var(--gold)" : "var(--dust-2)",
                  background: selectedGenre===g ? "var(--gold)" : "transparent",
                  color: selectedGenre===g ? "#fff" : "var(--muted)",
                  fontSize:"0.78rem",fontWeight:selectedGenre===g?700:500,
                  cursor:"pointer",transition:"all 0.15s",
                  whiteSpace:"nowrap",flexShrink:0,
                }}>{g}</button>
              ))}
            </div>

            {/* Sort — always visible inline on desktop */}
            <div style={{ flexShrink:0,padding:"0 1rem",borderLeft:"1px solid var(--card-border)",height:"40px",display:"flex",alignItems:"center" }}
              className="eb-sort-desktop"
            >
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{
                padding:"4px 8px",borderRadius:"var(--radius)",border:"1px solid var(--dust)",
                background:"var(--input-bg)",color:"var(--ink)",fontSize:"0.76rem",outline:"none",cursor:"pointer",
              }}>
                <option value="default">Default</option>
                <option value="price-asc">Price ↑</option>
                <option value="price-desc">Price ↓</option>
              </select>
            </div>
          </div>

          {/* Sort — mobile only, full-width row below pills */}
          <div className="eb-sort-mobile" style={{ display:"none",padding:"8px 1rem",borderBottom:"none" }}>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{
              width:"100%",padding:"8px 12px",borderRadius:"var(--radius)",
              border:"1px solid var(--dust)",background:"var(--input-bg)",
              color:"var(--ink)",fontSize:"0.82rem",outline:"none",cursor:"pointer",
            }}>
              <option value="default">Sort: Default</option>
              <option value="price-asc">Price: Low → High</option>
              <option value="price-desc">Price: High → Low</option>
            </select>
          </div>

        </div>
        <style>{`
          .eb-scroll-hide::-webkit-scrollbar { display: none; }
          @media (max-width: 600px) {
            .eb-sort-desktop { display: none !important; }
            .eb-sort-mobile  { display: block !important; }
          }
        `}</style>
      </div>

      {/* ── GRID ── */}
      <main style={{ maxWidth:"var(--max-w)",margin:"0 auto",padding:"2.5rem 2rem 6rem" }}>
        <p style={{ fontSize:"0.82rem",color:"var(--muted)",marginBottom:"1.5rem" }}>
          {filtered.length} {filtered.length===1?"title":"titles"} found
        </p>
        {filtered.length === 0 ? (
          <div style={{ textAlign:"center",padding:"5rem 2rem",color:"var(--muted)" }}>
            <AlertCircle size={40} style={{ margin:"0 auto 1rem",opacity:0.4 }} />
            <p>No books in this category.</p>
          </div>
        ) : (
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:"1.5rem" }}>
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} onAdd={addToCart} openCart={openCart} />
            ))}
          </div>
        )}
      </main>
    </>
  );
}
